/**
 * Access Area Configuration — Single Source of Truth
 *
 * All access area definitions, route mappings, and constants live here.
 * Guards, sidebar, and directives all reference this file.
 */

/** The 8 hotel access areas */
export enum AccessArea {
  Bookings = 'bookings',
  AvailabilityPricing = 'availability-pricing',
  HotelProfile = 'hotel-profile',
  PoliciesPayments = 'policies-payments',
  ContentMedia = 'content-media',
  Documents = 'documents',
  UserManagement = 'user-management',
  Analytics = 'analytics',
}

/** Internal HotelCard staff roles */
export enum InternalRole {
  SuperAdmin = 'super-admin',
  CustomerSuccess = 'customer-success',
  Viewer = 'viewer-system',
}

/**
 * Routes that are always accessible to any authenticated user.
 * No access area or internal role required.
 */
export const ALWAYS_VISIBLE_ROUTES: string[] = [
  'dashboard',
  'access-denied',
  'help',
  'setup-incomplete',
];

/**
 * Routes only accessible to internal HotelCard staff.
 * Hotel users are blocked entirely.
 *
 * Value is true for "any internal role" or a specific InternalRole for restricted access.
 */
export const INTERNAL_ONLY_ROUTES: Record<string, true | InternalRole> = {
  'seo-content': true,
  'hotel-settings': true,
  'documents': InternalRole.SuperAdmin,
  'extranet-users': true,
  'all-hotel-managers': InternalRole.SuperAdmin,
  'concierge-knowledge-base': true,
  'onboarding-requests': true,
  'hotel-creation': InternalRole.SuperAdmin,
  'all-bookings': InternalRole.SuperAdmin,
  'all-hotels': InternalRole.SuperAdmin,
  'ari-monitor': true,
  'ari-log': true,
  'booking-logs': InternalRole.SuperAdmin,
  'channel-manager-logs': InternalRole.SuperAdmin,
  'activity-logs': InternalRole.SuperAdmin,
  'usp': InternalRole.SuperAdmin,
};

/**
 * Maps route paths to the access area required for hotel users.
 * Internal users bypass this map entirely (they see all pages).
 * Routes not in this map, ALWAYS_VISIBLE_ROUTES, or INTERNAL_ONLY_ROUTES
 * will be denied by the guard (fail-safe).
 */
export const ROUTE_ACCESS_MAP: Record<string, AccessArea> = {
  // Analytics
  'analytics': AccessArea.Analytics,

  // Bookings
  'bookings': AccessArea.Bookings,
  'bookings/:id': AccessArea.Bookings,
  // MVP: Booking guarantee not in scope
  // 'failed-guarantees': AccessArea.Bookings,

  // Availability & Pricing
  'availability': AccessArea.AvailabilityPricing,
  'yielding-tools': AccessArea.AvailabilityPricing,
  'mlos': AccessArea.AvailabilityPricing,
  'extra-beds': AccessArea.AvailabilityPricing,
  'services': AccessArea.AvailabilityPricing,

  // Hotel Profile
  'hotel-information': AccessArea.HotelProfile,
  'contact-information': AccessArea.HotelProfile,
  'rooms': AccessArea.HotelProfile,
  'add-room': AccessArea.HotelProfile,
  'room-edit/:id': AccessArea.HotelProfile,
  'amenities': AccessArea.HotelProfile,
  'activities': AccessArea.HotelProfile,
  'themes': AccessArea.HotelProfile,

  // Policies & Payments
  'cancelation-policy': AccessArea.PoliciesPayments,
  'payments': AccessArea.PoliciesPayments,
  'messages': AccessArea.PoliciesPayments,

  // Content & Media
  'hotel-description': AccessArea.ContentMedia,
  'hotel-gallery': AccessArea.ContentMedia,

  // Documents (hotel-specific)
  'hotel-documents': AccessArea.Documents,

  // User Management
  'hotel-managers': AccessArea.UserManagement,
  'hotel-managers/create': AccessArea.UserManagement,
  'hotel-managers/add-existing': AccessArea.UserManagement,
  'hotel-managers/edit/:userId': AccessArea.UserManagement,
};
